import React, { useState, useRef } from "react";
import { FaLightbulb } from "react-icons/fa";
import InviteButton from "../inviteButton/inviteButton";
import "./ProfileCard.css";

const ProfileCard = ({ profile, onProfileClick, onAnalyze, onInvite, summary }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const scrollRef = useRef(null);

  const identity = (profile.identity || "").replace(/^"(.*)"$/, "$1").trim() || "Builder";
  const skills = profile.skills || [];
  const tools = profile.tools || [];
  const openRoles = profile.openRoles || [];

  return (
    <div className="flip-card w-80 h-72" onClick={() => onProfileClick(profile.email)}>
      <div className={`flip-card-inner ${isFlipped ? "flipped" : ""}`}>
        {/* FRONT */}
        <div className="flip-card-front rounded-2xl border border-neutral-200 px-4 py-5 text-left relative bg-white">

          {/* Lightbulb + Invite */}
          <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
            <div
              className="cursor-pointer"
              title="Analyze this builder"
              onClick={(e) => {
                e.stopPropagation();
                setIsFlipped(true);
                onAnalyze(profile.email);
              }}
            >
              <FaLightbulb className="text-yellow-400 hover:text-yellow-500" size={20} />
            </div>
            <div
              onClick={(e) => {
                e.stopPropagation();
                onInvite(profile);
              }}
            >
              <InviteButton />
            </div>
          </div>

          {/* 👤 Name + Location */}
          <div className="flex items-center gap-3 mb-1">
            <img
              src={profile.profileImage}
              alt={profile.displayName || "User"}
              className="w-12 h-12 rounded-full border border-white bg-gray-200 object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/person.png";
              }}
            />
            <div className="flex flex-col justify-center text-left max-w-[calc(100%-3rem-1.5rem-2.5rem)]">

              <div
                className="text-black font-medium font-['Inter'] leading-tight tracking-tight"
                style={{
                    maxWidth: "180px",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: "1.2",
                    display: "inline-block",
                }}
            >
                {(() => {
                    const name = profile.displayName || "Unknown";
                    const maxLineLength = 20;

                    if (name.length <= maxLineLength) {
                        return name; // ✅ One-liner
                    }

                    const parts = name.split(" ");
                    if (parts.length === 1) {
                        return name; // ✅ No spaces, just break naturally
                    }

                    const firstName = parts.slice(0, -1).join(" ");
                    const lastName = parts.slice(-1).join(" ");

                    return (
                        <>
                            <div>{firstName}</div>
                            <div>{lastName}</div>
                        </>
                    );
                })()}
            </div>

              {(profile.city || profile.country) && (
                <div className="text-neutral-400 text-xs font-medium font-['Inter'] leading-tight tracking-tight">
                  {[profile.city, profile.country].filter(Boolean).join(", ")}
                </div>
              )}
            </div>
          </div>

          {/* 🎯 Identity */}
          <div className="mt-1 font-semibold text-black text-sm truncate">{identity}</div>

          {/* 🧠 Skills */}
          {skills.length > 0 ? (
            <div className="mt-[7px] pl-1 text-left flex items-center gap-2 max-w-[270px] overflow-hidden">
              <div className="px-2 h-7 flex items-center rounded shrink-0" style={{ backgroundColor: "#E6F3EB" }}>
                <span className="text-green-400 text-xs font-medium font-['Inter'] whitespace-nowrap">
                  Core skills:
                </span>
              </div>
             <div
                className="text-xs font-medium font-['Inter'] text-[#777777] break-words"
                style={{
                    maxWidth: "180px",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: "1.1",
                }}
            >
                {skills.slice(0, 2).join(", ")}
            </div>

            </div>
          ) : (
            <div className="mt-[7px] pl-1 text-left">
              <div
                className="inline-block px-3 py-1 rounded text-xs font-medium font-['Inter']"
                style={{ backgroundColor: "#E3F2FD", color: "#1565C0" }}
              >
                Discovering strengths
              </div>
            </div>
          )}

          {/* 🛠️ Tools */}
          {tools.length > 0 ? (
            <div className="mt-[6px] pl-1 text-left flex items-center gap-2 max-w-[270px] overflow-hidden">
              <div className="px-2 h-7 flex items-center rounded shrink-0" style={{ backgroundColor: "#E5EAFF" }}>
                <span className="text-blue-500 text-xs font-medium font-['Inter'] whitespace-nowrap">Tools:</span>
              </div>
              <div
                className="text-xs font-medium font-['Inter'] text-[#777777] whitespace-nowrap overflow-hidden text-ellipsis"
                style={{ maxWidth: "180px" }}
              >
                {tools.join(", ")}
              </div>
            </div>
          ) : (
            <div className="mt-[6px] pl-1 text-left">
              <div
                className="inline-block px-3 py-1 rounded text-xs font-medium font-['Inter']"
                style={{ backgroundColor: "#E5EAFF", color: "#3F51B5" }}
              >
                Tuning the toolbox
              </div>
            </div>
          )}

          {/* 🧡 Looking For */}
          {openRoles.length > 0 ? (
            <div className="mt-[6px] pl-1 text-left flex items-center gap-2 max-w-[270px] overflow-hidden">
              <div className="px-2 h-7 flex items-center rounded shrink-0" style={{ backgroundColor: "#FFF3E0" }}>
                <span className="text-orange-500 text-xs font-medium font-['Inter'] whitespace-nowrap">
                  Looking for:
                </span>
              </div>
              <div
                className="text-xs font-medium font-['Inter'] text-[#777777] break-words"
                style={{
                    maxWidth: "180px",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: "1.1",
                }}
              >
                {openRoles.join(", ")}
              </div>

            </div>
          ) : (
            <div className="mt-[6px] pl-1 text-left">
              <div
                className="inline-block px-3 py-1 rounded text-xs font-medium font-['Inter']"
                style={{ backgroundColor: "#FFF3E0", color: "#EF6C00" }}
              >
                Exploring vibes
              </div>
            </div>
          )}
        </div>

        {/* Codi Insights - Flip Side */}
        <div
            className="flip-card-back"
            onClick={(e) => {
                e.stopPropagation();
                setIsFlipped(false);
            }}
        >
            <div className="insight-layout">
                <div className="insight-header">
                    <img src="/CodiCharacter.png" alt="Codi" className="codi-avatar" />
                </div>

                <div className="insight-scroll-wrapper">
                    <div
                        className="scroll-arrow scroll-arrow-up"
                        onClick={(e) => {
                            e.stopPropagation();
                            scrollRef.current?.scrollBy({ top: -40, behavior: "smooth" });
                        }}
                    >
                        &#9650;
                    </div>

                    <div className="insight-scroll-content" ref={scrollRef}>
                        {summary?.replace(/^["']|["']$/g, "") || "Analyzing..."}
                    </div>

                    <div
                        className="scroll-arrow scroll-arrow-down"
                        onClick={(e) => {
                            e.stopPropagation();
                            scrollRef.current?.scrollBy({ top: 40, behavior: "smooth" });
                        }}
                    >
                        &#9660;
                    </div>
                </div>

                <div className="return-hint">Click anywhere to return</div>
            </div>
        </div>

      </div>
    </div>
  );
};

export default ProfileCard;
